import type { ReactNode } from "react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Info } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { DataTable, type ColumnDef } from "@bastion/table";
import { renderDashboardTemplate } from "../lib/renderDashboardTemplate";
import {
  resolveRowValueExpression,
  type DashboardParameterValue,
  type DashboardWidget,
  type StatStripCellConfig,
  type WidgetDataResult,
  type WidgetDataRow,
  type WidgetDeltaPolarity,
  type WidgetInteractionPointContext,
  type WidgetValueFormat,
} from "../types";
import {
  ChartContainer,
  ChartTooltip,
  HeatmapSkeleton,
  NumberSkeleton,
  SEMANTIC_COLORS,
  TableSkeleton,
  colorForSeries,
  computeDelta,
  formatAxisTick,
  formatTimestamp,
  formatValue,
  isLikelyTimestamp,
  type DeltaResult,
  type FormatOptions,
} from "../lib";
import { CalendarHeatmap } from "./CalendarHeatmap";
import { MatrixHeatmap } from "./MatrixHeatmap";
import styles from "./WidgetRenderer.module.css";

export interface WidgetRendererProps {
  widget: DashboardWidget;
  data: WidgetDataResult;
  onInteraction?: (row: WidgetDataRow, point?: WidgetInteractionPointContext) => void;
  params?: Record<string, DashboardParameterValue>;
  headerActions?: ReactNode;
}

/**
 * SVG chart attributes from the design system chart tokens
 * (conventions/design/README.md, "Data and numbers" > "Charts"). SVG
 * paint accepts `var()`, so each chart follows the app theme.
 */
const CHART_GRID = "var(--chart-grid)";
const CHART_TICK = "var(--chart-tick)";
const CHART_BASELINE = "var(--chart-baseline)";
const CHART_TICK_FONT_SIZE = "var(--fs-micro)";
const CHART_TICK_FONT_FAMILY = "var(--font-mono)";
/**
 * Recharts measures tick text in the sans face to drop ticks that
 * collide. Mono ticks are wider, so the category axes keep a larger gap.
 */
const CHART_TICK_MIN_GAP = 12;
/**
 * Category axes label every Nth tick, with one N for the whole axis, so a
 * date axis has even steps. The default interval drops ticks one at a
 * time by label width, so "Sep 9" and "Oct 1" stay and "Sep 10" goes.
 */
const CHART_CATEGORY_TICK_INTERVAL = "equidistantPreserveStart";
/** Tooltip cursor: a band on bars, a line on area charts. */
const CHART_CURSOR_BAND = { fill: "var(--surface-hover)" };
const CHART_CURSOR_LINE = { stroke: CHART_BASELINE };
/**
 * Ring around the hovered point. Recharts draws it in a fixed `#fff`,
 * which shows as a white ring on the dark card. `--surface` is the card
 * ground in both themes.
 */
const CHART_ACTIVE_DOT_RING = "var(--surface)";

function CardShell({
  title,
  subtitle,
  description,
  headerActions,
  kpi,
  children,
}: {
  title?: string;
  subtitle?: string;
  description?: string;
  headerActions?: ReactNode;
  /** KPI layout: the title is the KPI label, with no rule under it. */
  kpi?: boolean;
  children: ReactNode;
}) {
  const showHeader = title || subtitle || headerActions;
  return (
    <section className={kpi ? `${styles.card} ${styles.cardKpi}` : styles.card}>
      {showHeader ? (
        <div className={styles.titleRow}>
          <div className={styles.titleWithInfo}>
            {title ? <h3 className={styles.title}>{title}</h3> : <span />}
            {description ? <WidgetInfoHint title={title} description={description} /> : null}
          </div>
          <div className={styles.titleRowActions}>
            {subtitle ? <span className={styles.subtitle}>{subtitle}</span> : null}
            {headerActions}
          </div>
        </div>
      ) : null}
      <div className={styles.cardBody}>{children}</div>
    </section>
  );
}

/**
 * The info hint after a widget title (design system README, "App
 * components", Info hint). Mouse hover shows the panel through CSS. A tap
 * toggles the panel, and keyboard focus opens it. A press outside, Escape
 * or a blur closes it.
 */
function WidgetInfoHint({ title, description }: { title?: string; description: string }) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  // The pointer of the last press on the button. A mouse click does not
  // toggle the panel, because hover shows it.
  const pointerTypeRef = useRef("");
  const panelId = useId();

  useEffect(() => {
    if (!open) return;
    const closeOnOutsidePress = (event: PointerEvent) => {
      if (!buttonRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", closeOnOutsidePress);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsidePress);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  return (
    <button
      ref={buttonRef}
      type="button"
      className={styles.infoWrap}
      aria-label={title ? `About ${title}` : "About this widget"}
      aria-describedby={panelId}
      aria-expanded={open}
      onPointerDown={(event) => {
        pointerTypeRef.current = event.pointerType;
      }}
      onClick={() => {
        const pointerType = pointerTypeRef.current;
        pointerTypeRef.current = "";
        if (pointerType === "mouse") return;
        setOpen((current) => !current);
      }}
      onFocus={(event) => {
        if (event.currentTarget.matches(":focus-visible")) setOpen(true);
      }}
      onBlur={() => setOpen(false)}
    >
      <Info className={styles.infoIcon} aria-hidden />
      <span id={panelId} className={styles.infoPanel}>
        {description}
      </span>
    </button>
  );
}

function ErrorPanel({ message }: { message: string }) {
  return <div className={styles.errorPanel}>{message}</div>;
}

const BIG_NUMBER_FORMAT_MAP: Record<
  NonNullable<Extract<DashboardWidget, { type: "big_number" }>["config"]["format"]>,
  FormatOptions["format"]
> = {
  number: "auto",
  percent: "percent",
  currency: "currency",
};

export function WidgetRenderer({ widget, data, onInteraction, params, headerActions }: WidgetRendererProps) {
  const renderedTitle = renderDashboardTemplate(widget.title, params);
  const description = renderDashboardTemplate(widget.description, params);

  // Errors must take precedence over the markdown branch. DashboardWidgetTile
  // signals unmet `requires` parameters by passing an Error in `data.error`,
  // and a markdown widget that templates `{{ collector_name }}` should show
  // the "Select collector_name to view this widget" message — not render an
  // empty string where the unset parameter would have been.
  if (data.error) {
    // A KPI tile keeps the KPI header in the error state, so the tiles in
    // one row have the same header.
    const kpi =
      widget.type === "big_number" ||
      widget.type === "stat_with_delta" ||
      widget.type === "sparkline_stat";
    return (
      <CardShell
        kpi={kpi}
        title={renderedTitle}
        description={description}
        headerActions={headerActions}
      >
        <ErrorPanel message={data.error.message} />
      </CardShell>
    );
  }

  if (widget.type === "markdown") {
    const text = renderDashboardTemplate(widget.config.text, params) ?? "";
    return (
      <CardShell title={renderedTitle} description={description} headerActions={headerActions}>
        <div className={styles.markdown}>
          {/* `remark-gfm` enables tables, task lists, strikethrough, and
              autolinks — the GitHub-flavored Markdown features dashboard
              authors expect when they paste a snippet from a Notion doc
              or PR description. */}
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
        </div>
      </CardShell>
    );
  }

  switch (widget.type) {
    case "big_number": {
      const value = data.rows[0]?.[widget.config.value];
      const hasValue = value !== null && value !== undefined;
      const subtitle = data.rows.length > 0 ? widget.config.value : undefined;
      const format = BIG_NUMBER_FORMAT_MAP[widget.config.format ?? "number"];
      return (
        <CardShell
          kpi
          title={renderedTitle}
          subtitle={subtitle}
          description={description}
          headerActions={headerActions}
        >
          {data.isLoading ? (
            <NumberSkeleton />
          ) : hasValue ? (
            <div className={styles.bigNumberValue}>{formatValue(value, { format })}</div>
          ) : (
            <span className={styles.bigNumberPlaceholder}>No value returned</span>
          )}
        </CardShell>
      );
    }

    case "line": {
      return (
        <CardShell
          title={renderedTitle}
          description={description}
          headerActions={headerActions}
          subtitle={
            widget.config.y.length === 1
              ? widget.config.y[0]
              : `${widget.config.y.length} series`
          }
        >
          <ChartContainer
            isLoading={data.isLoading}
            isEmpty={data.rows.length === 0}
            emptyMessage="No rows returned for this query."
          >
            {() => <LineAreaChart widget={widget} rows={data.rows} />}
          </ChartContainer>
          {data.isLoading || data.rows.length > 0 ? (
            <SeriesLegend series={widget.config.y} />
          ) : null}
        </CardShell>
      );
    }

    case "table": {
      // Mirror the big_number / line cases: only show the row count once
      // we actually have data. Otherwise the subtitle reads "0 rows"
      // during the initial fetch and snaps to the real count when the
      // query lands, which reads as a glitch.
      const tableSubtitle = data.isLoading ? undefined : `${data.rows.length} rows`;
      return (
        <CardShell title={renderedTitle} subtitle={tableSubtitle} description={description} headerActions={headerActions}>
          <div className={styles.tableWrap}>
            {data.isLoading ? (
              <TableSkeleton rows={5} columns={Math.max(1, data.columns.length || 4)} />
            ) : (
              <SqlTable
                columns={data.columns}
                rows={data.rows}
                hiddenColumns={widget.config.hidden_columns}
                onRowClick={
                  widget.interactions?.some((interaction) => interaction.event === "row_click")
                    ? onInteraction
                    : undefined
                }
                getRowClassName={(row) => (isSelectedRow(widget, row, params) ? styles.selectedRow : undefined)}
              />
            )}
          </div>
        </CardShell>
      );
    }

    case "stat_with_delta": {
      const row = data.rows[0];
      const value = row?.[widget.config.value];
      const compare =
        widget.config.compare_value !== undefined ? row?.[widget.config.compare_value] : undefined;
      const delta = compare !== undefined ? computeDelta(value, compare) : null;
      const polarity = widget.config.delta_polarity ?? "increase_is_good";
      const compareLabel =
        widget.config.compare_value !== undefined
          ? widget.config.compare_label ?? "vs prior period"
          : undefined;
      const hasValue = value !== null && value !== undefined;
      return (
        <CardShell kpi title={renderedTitle} description={description} headerActions={headerActions}>
          {data.isLoading ? (
            <NumberSkeleton />
          ) : hasValue ? (
            <div className={styles.statBlock}>
              <div className={styles.statValue}>
                {formatValue(value, { format: widget.config.format })}
              </div>
              {delta ? (
                <DeltaLine
                  delta={delta}
                  polarity={polarity}
                  format={widget.config.format}
                  label={compareLabel}
                />
              ) : null}
            </div>
          ) : (
            <span className={styles.bigNumberPlaceholder}>No value returned</span>
          )}
        </CardShell>
      );
    }

    case "sparkline_stat": {
      return (
        <CardShell kpi title={renderedTitle} description={description} headerActions={headerActions}>
          <SparklineStatBody widget={widget} data={data} />
        </CardShell>
      );
    }

    case "stat_strip": {
      const compareLabel = widget.config.compare_label ?? "vs prior period";
      // Each cell either reads from its own row (rows_as_cells) or from
      // the first (and presumably only) row. Render a stable list of
      // cells either way so React's key stays consistent across loads.
      const renderedCells = data.isLoading
        ? widget.config.cells.map((cell) => ({ cell, row: undefined as WidgetDataRow | undefined }))
        : widget.config.rows_as_cells
          ? widget.config.cells.map((cell, idx) => ({ cell, row: data.rows[idx] }))
          : widget.config.cells.map((cell) => ({ cell, row: data.rows[0] }));
      return (
        <CardShell title={renderedTitle} description={description} headerActions={headerActions}>
          <div className={styles.statStrip}>
            {renderedCells.map(({ cell, row }) => (
              <StatStripCell
                key={cell.id}
                cell={cell}
                row={row}
                compareLabel={cell.compare_value ? compareLabel : undefined}
                isLoading={data.isLoading}
              />
            ))}
          </div>
        </CardShell>
      );
    }

    case "calendar_heatmap": {
      const weeks = widget.config.weeks ?? 26;
      const subtitle = data.isLoading ? undefined : `last ${weeks} weeks`;
      return (
        <CardShell
          title={renderedTitle}
          subtitle={subtitle}
          description={description}
          headerActions={headerActions}
        >
          {data.isLoading ? (
            <HeatmapSkeleton minHeight={120} />
          ) : (
            <CalendarHeatmap widget={widget} rows={data.rows} />
          )}
        </CardShell>
      );
    }

    case "bar": {
      const seriesCount = widget.config.y.length;
      const subtitle =
        seriesCount === 1
          ? widget.config.y[0]
          : `${seriesCount} series${widget.config.stacked ? " · stacked" : ""}`;
      const supportsPointClick = widget.interactions?.some((i) => i.event === "point_click");
      return (
        <CardShell
          title={renderedTitle}
          description={description}
          headerActions={headerActions}
          subtitle={subtitle}
        >
          <ChartContainer
            isLoading={data.isLoading}
            isEmpty={data.rows.length === 0}
            emptyMessage="No rows returned for this query."
          >
            {() => (
              <BarChartBody
                widget={widget}
                rows={data.rows}
                onBarSegmentClick={
                  supportsPointClick && onInteraction
                    ? (row, seriesKey) => onInteraction(row, { seriesKey })
                    : undefined
                }
              />
            )}
          </ChartContainer>
          {data.isLoading || data.rows.length > 0 ? (
            <SeriesLegend series={widget.config.y} />
          ) : null}
        </CardShell>
      );
    }

    case "stacked_area": {
      const seriesCount = widget.config.y.length;
      const subtitle =
        seriesCount === 1 ? widget.config.y[0] : `${seriesCount} series · stacked`;
      return (
        <CardShell
          title={renderedTitle}
          description={description}
          headerActions={headerActions}
          subtitle={subtitle}
        >
          <ChartContainer
            isLoading={data.isLoading}
            isEmpty={data.rows.length === 0}
            emptyMessage="No rows returned for this query."
          >
            {() => <StackedAreaChartBody widget={widget} rows={data.rows} />}
          </ChartContainer>
          {data.isLoading || data.rows.length > 0 ? (
            <SeriesLegend series={widget.config.y} />
          ) : null}
        </CardShell>
      );
    }

    case "heatmap": {
      const valueFormat: WidgetValueFormat = widget.config.value_format ?? "auto";
      // The matrix renderer owns its own empty-state copy because it
      // distinguishes between "query returned zero rows" (handled inside)
      // and "config picked an axis column the SQL never SELECTed".
      // Keep the loading branch at the WidgetRenderer level so the
      // skeleton's geometry doesn't depend on row data we haven't seen
      // yet.
      const subtitle = data.isLoading
        ? undefined
        : `${widget.config.y} × ${widget.config.x}`;
      return (
        <CardShell
          title={renderedTitle}
          subtitle={subtitle}
          description={description}
          headerActions={headerActions}
        >
          {data.isLoading ? (
            <HeatmapSkeleton minHeight={200} />
          ) : data.rows.length === 0 ? (
            <p className={styles.matrixHeatmapEmpty}>No rows returned for this query.</p>
          ) : (
            <MatrixHeatmap
              rows={data.rows}
              xKey={widget.config.x}
              yKey={widget.config.y}
              valueKey={widget.config.value}
              valueFormat={valueFormat}
              cellMinWidth={widget.config.cell_min_width}
            />
          )}
        </CardShell>
      );
    }

    case "histogram": {
      const subtitle = data.isLoading ? undefined : `${data.rows.length} bins`;
      return (
        <CardShell
          title={renderedTitle}
          subtitle={subtitle}
          description={description}
          headerActions={headerActions}
        >
          <ChartContainer
            isLoading={data.isLoading}
            isEmpty={data.rows.length === 0}
            emptyMessage="No rows returned for this query."
          >
            {() => <HistogramChartBody widget={widget} rows={data.rows} />}
          </ChartContainer>
        </CardShell>
      );
    }

    case "entity_picker": {
      const subtitle = data.isLoading ? undefined : `${data.rows.length} ${data.rows.length === 1 ? "entity" : "entities"}`;
      return (
        <CardShell title={renderedTitle} subtitle={subtitle} description={description} headerActions={headerActions}>
          <EntityPickerBody
            widget={widget}
            data={data}
            params={params}
            onInteraction={onInteraction}
          />
        </CardShell>
      );
    }

    default: {
      const _exhaustive: never = widget;
      return _exhaustive;
    }
  }
}

/**
 * Legend under a chart with more than one series (design system README,
 * "Charts"): an 8px --swatch square in the series colour and the series
 * name. Series 2 to 4 are ink and greys and series 5 and 6 are green
 * tints, so the legend is the only way to tell the series apart without
 * a hover. A chart with one series has no legend.
 */
function SeriesLegend({ series }: { series: readonly string[] }) {
  if (series.length < 2) return null;
  return (
    <ul className={styles.legend} aria-label="Series">
      {series.map((name, index) => (
        <li key={name} className={styles.legendItem}>
          <span
            className={styles.legendSwatch}
            style={{ background: colorForSeries(index) }}
            aria-hidden
          />
          <span className={styles.legendName}>{name}</span>
        </li>
      ))}
    </ul>
  );
}

function deltaColor(direction: -1 | 0 | 1, polarity: WidgetDeltaPolarity): string {
  if (direction === 0) return SEMANTIC_COLORS.muted;
  if (polarity === "neutral") return SEMANTIC_COLORS.muted;
  // increase_is_good: positive direction is good (success), negative is bad (error).
  // decrease_is_good: flipped. Both colours are --text-2 (see SEMANTIC_COLORS),
  // so the arrow states the direction.
  const goodWhenPositive = polarity === "increase_is_good";
  const isGood = goodWhenPositive ? direction > 0 : direction < 0;
  return isGood ? SEMANTIC_COLORS.success : SEMANTIC_COLORS.error;
}

function deltaArrow(direction: -1 | 0 | 1): string {
  if (direction > 0) return "↑";
  if (direction < 0) return "↓";
  return "·";
}

interface DeltaLineProps {
  delta: DeltaResult;
  polarity: WidgetDeltaPolarity;
  format?: WidgetValueFormat;
  label?: string;
}

function DeltaLine({ delta, polarity, format, label }: DeltaLineProps) {
  const color = deltaColor(delta.direction, polarity);
  const arrow = deltaArrow(delta.direction);
  // Prefer the percentage when available (matches Datadog / Honeycomb
  // tile conventions). Fall back to the absolute change when the
  // previous value was zero, so the row still reads as informative
  // instead of "—".
  const body =
    delta.fraction !== null
      ? `${formatValue(Math.abs(delta.fraction) * 100, { format: "percent" })}`
      : formatValue(Math.abs(delta.absolute), { format });
  return (
    <div className={styles.statDelta} style={{ color }}>
      <span aria-hidden>{arrow}</span>
      <span>{body}</span>
      {label ? <span className={styles.statDeltaLabel}>{label}</span> : null}
    </div>
  );
}

interface SparklineStatBodyProps {
  widget: Extract<DashboardWidget, { type: "sparkline_stat" }>;
  data: WidgetDataResult;
}

function SparklineStatBody({ widget, data }: SparklineStatBodyProps) {
  const seriesKey = widget.config.series.y;
  const xKey = widget.config.series.x;
  const seriesRows = useMemo(() => {
    return data.rows.filter((r) => {
      const y = r[seriesKey];
      return y !== null && y !== undefined && Number.isFinite(Number(y));
    });
  }, [data.rows, seriesKey]);
  if (data.isLoading) return <NumberSkeleton />;
  const row = data.rows[0];
  const value = row?.[widget.config.value];
  const compare =
    widget.config.compare_value !== undefined ? row?.[widget.config.compare_value] : undefined;
  const delta = compare !== undefined ? computeDelta(value, compare) : null;
  const polarity = widget.config.delta_polarity ?? "increase_is_good";
  const compareLabel =
    widget.config.compare_value !== undefined
      ? widget.config.compare_label ?? "vs prior period"
      : undefined;
  const hasValue = value !== null && value !== undefined;
  if (!hasValue) {
    return <span className={styles.bigNumberPlaceholder}>No value returned</span>;
  }
  const sparkColor = colorForSeries(0);
  return (
    <div className={styles.statBlock}>
      <div className={styles.statValue}>
        {formatValue(value, { format: widget.config.format })}
      </div>
      {delta ? (
        <DeltaLine
          delta={delta}
          polarity={polarity}
          format={widget.config.format}
          label={compareLabel}
        />
      ) : null}
      <div className={styles.sparklineWrap}>
        {/*
         * `dense` mode would composite chartWrapDense (min-height only) and
         * leave ResponsiveContainer with no flexbox parent, which renders as
         * a 0×0 SVG in jsdom. Use the standard chartWrap (width: 100%, flex:
         * 1) with an inline minHeight override so the sparkline still sits
         * at ~60px while ResponsiveContainer has a measurable parent.
         */}
        <ChartContainer
          isLoading={false}
          isEmpty={seriesRows.length === 0}
          emptyMessage=""
          minHeight={60}
        >
          {() => (
            <AreaChart
              data={seriesRows as unknown as object[]}
              margin={{ top: 4, right: 0, left: 0, bottom: 0 }}
            >
              <XAxis dataKey={xKey} hide />
              <YAxis hide domain={["auto", "auto"]} />
              <Area
                type="monotone"
                dataKey={seriesKey}
                stroke={sparkColor}
                strokeWidth={1.5}
                // A flat fill: the design system has no gradients.
                fill={sparkColor}
                fillOpacity={0.12}
                dot={false}
                activeDot={false}
                isAnimationActive={false}
              />
            </AreaChart>
          )}
        </ChartContainer>
      </div>
    </div>
  );
}

interface StatStripCellProps {
  cell: StatStripCellConfig;
  row: WidgetDataRow | undefined;
  compareLabel: string | undefined;
  isLoading: boolean;
}

function StatStripCell({ cell, row, compareLabel, isLoading }: StatStripCellProps) {
  const value = row?.[cell.value];
  const compare = cell.compare_value ? row?.[cell.compare_value] : undefined;
  const delta = compare !== undefined ? computeDelta(value, compare) : null;
  const polarity = cell.delta_polarity ?? "increase_is_good";
  const hasValue = value !== null && value !== undefined;
  return (
    <div className={styles.statStripCell}>
      <div className={styles.statStripLabel}>{cell.label}</div>
      {isLoading ? (
        <NumberSkeleton />
      ) : (
        <>
          <div className={styles.statStripValue}>
            {hasValue ? formatValue(value, { format: cell.format }) : "—"}
          </div>
          {delta ? (
            <DeltaLine
              delta={delta}
              polarity={polarity}
              format={cell.format}
              label={compareLabel}
            />
          ) : null}
        </>
      )}
    </div>
  );
}

interface LineAreaChartProps {
  widget: Extract<DashboardWidget, { type: "line" }>;
  rows: WidgetDataRow[];
}

function LineAreaChart({ widget, rows }: LineAreaChartProps) {
  // With more than one series the flat fills stack into one opaque block
  // and hide the lower lines, so only a single-series line has a fill.
  const hasFill = widget.config.y.length === 1;
  return (
    <AreaChart data={rows as unknown as object[]} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
      <CartesianGrid stroke={CHART_GRID} vertical={false} />
      <XAxis
        dataKey={widget.config.x}
        tickFormatter={(value) => formatAxisTick(value)}
        tickMargin={8}
        axisLine={{ stroke: CHART_BASELINE }}
        tickLine={false}
        stroke={CHART_TICK}
        fontSize={CHART_TICK_FONT_SIZE}
        fontFamily={CHART_TICK_FONT_FAMILY}
        minTickGap={CHART_TICK_MIN_GAP}
        interval={CHART_CATEGORY_TICK_INTERVAL}
      />
      <YAxis
        tickFormatter={(value) => formatAxisTick(value, { format: "compact" })}
        tickMargin={8}
        axisLine={false}
        tickLine={false}
        stroke={CHART_TICK}
        fontSize={CHART_TICK_FONT_SIZE}
        fontFamily={CHART_TICK_FONT_FAMILY}
        width={40}
      />
      <Tooltip
        content={<ChartTooltip valueFormat="auto" />}
        cursor={CHART_CURSOR_LINE}
      />
      {widget.config.y.map((field, index) => {
        const color = colorForSeries(index);
        return (
          <Area
            key={field}
            type="monotone"
            dataKey={field}
            stroke={color}
            strokeWidth={2}
            // A flat fill: the design system has no gradients.
            fill={hasFill ? color : "none"}
            fillOpacity={hasFill ? 0.1 : 0}
            dot={{ r: 3, stroke: color, strokeWidth: 1.5, fill: "var(--surface)" }}
            activeDot={{ r: 4, stroke: CHART_ACTIVE_DOT_RING }}
            name={field}
            isAnimationActive={false}
          />
        );
      })}
    </AreaChart>
  );
}

interface BarChartBodyProps {
  widget: Extract<DashboardWidget, { type: "bar" }>;
  rows: WidgetDataRow[];
  onBarSegmentClick?: (row: WidgetDataRow, seriesKey: string) => void;
}

/**
 * Recharts `<BarChart>` wrapper for the categorical `bar` widget kind.
 *
 * Recharts toggles between vertical and horizontal layouts via the
 * `layout` prop, but the *meaning* of the `<XAxis>` and `<YAxis>` flips
 * with it. In `layout="vertical"` (the horizontal-bars layout), the
 * value axis becomes the X axis and the category axis becomes the Y
 * axis — so the dataKey assignments and tick formatters have to flip
 * with the orientation. Keeping that mapping in one place avoids the
 * very-easy-to-miss mistake of leaving a numeric tickFormatter on the
 * categorical axis once a chart is rotated.
 */
function BarChartBody({ widget, rows, onBarSegmentClick }: BarChartBodyProps) {
  const orientation = widget.config.orientation ?? "vertical";
  const isHorizontal = orientation === "horizontal";
  const valueFormat: WidgetValueFormat = widget.config.value_format ?? "auto";
  const stackId = widget.config.stacked ? "stack" : undefined;
  // Recharts: `layout="vertical"` means *bars are horizontal* (the
  // value axis runs left-to-right). When the widget config asks for
  // horizontal bars, we set the chart layout to vertical.
  const chartLayout = isHorizontal ? "vertical" : "horizontal";
  const categoryAxisProps = {
    dataKey: widget.config.x,
    tickFormatter: (value: unknown) => formatAxisTick(value),
    tickMargin: 8,
    // The category axis is the zero line of the bars: --chart-baseline.
    axisLine: { stroke: CHART_BASELINE },
    tickLine: false,
    stroke: CHART_TICK,
    fontSize: CHART_TICK_FONT_SIZE,
    fontFamily: CHART_TICK_FONT_FAMILY,
    minTickGap: CHART_TICK_MIN_GAP,
    interval: CHART_CATEGORY_TICK_INTERVAL,
  } as const;
  const valueAxisProps = {
    tickFormatter: (value: unknown) =>
      formatAxisTick(value, { format: valueFormat === "auto" ? "compact" : valueFormat }),
    tickMargin: 8,
    axisLine: false,
    tickLine: false,
    stroke: CHART_TICK,
    fontSize: CHART_TICK_FONT_SIZE,
    fontFamily: CHART_TICK_FONT_FAMILY,
  } as const;
  return (
    <BarChart
      data={rows as unknown as object[]}
      layout={chartLayout}
      margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
    >
      <CartesianGrid
        stroke={CHART_GRID}
        // Only draw grid lines parallel to the value axis — gridlines
        // crossing the categorical axis read as noise on bar charts.
        horizontal={!isHorizontal}
        vertical={isHorizontal}
      />
      {isHorizontal ? (
        <>
          <XAxis type="number" {...valueAxisProps} width={undefined} />
          {/* Allocate enough room for category labels — long collector
              names / hardware versions otherwise truncate to "..." with
              no tooltip surface area. 120px matches what Datadog uses
              for horizontal-bar leaderboards. */}
          <YAxis type="category" {...categoryAxisProps} width={120} />
        </>
      ) : (
        <>
          <XAxis type="category" {...categoryAxisProps} />
          <YAxis type="number" {...valueAxisProps} width={48} />
        </>
      )}
      <Tooltip
        content={<ChartTooltip valueFormat={valueFormat} />}
        cursor={CHART_CURSOR_BAND}
      />
      {widget.config.y.map((field, index) => {
        const color = colorForSeries(index);
        return (
          <Bar
            key={field}
            dataKey={field}
            fill={color}
            stackId={stackId}
            name={field}
            cursor={onBarSegmentClick ? "pointer" : undefined}
            onClick={
              onBarSegmentClick
                ? (state: unknown) => {
                    const payload = (state as { payload?: WidgetDataRow }).payload;
                    if (!payload) return;
                    onBarSegmentClick(payload, field);
                  }
                : undefined
            }
            // Match the line widget's choice: skip the entrance tween so
            // dashboards with many widgets don't shimmer through a
            // staggered animation on every refetch.
            isAnimationActive={false}
            // A 2px corner (--radius-2xs) on the end of the bar. Stacked
            // segments stay square.
            radius={
              widget.config.stacked
                ? 0
                : isHorizontal
                  ? [0, 2, 2, 0]
                  : [2, 2, 0, 0]
            }
          />
        );
      })}
    </BarChart>
  );
}

interface StackedAreaChartBodyProps {
  widget: Extract<DashboardWidget, { type: "stacked_area" }>;
  rows: WidgetDataRow[];
}

/**
 * Stacked-area variant of the `line` widget. Every series shares the
 * same `stackId` so the top edge of the chart represents the running
 * total of every series at each x — the canonical reading of an
 * area-stack ("composition over time").
 */
function StackedAreaChartBody({ widget, rows }: StackedAreaChartBodyProps) {
  const valueFormat: WidgetValueFormat = widget.config.value_format ?? "auto";
  return (
    <AreaChart data={rows as unknown as object[]} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
      <CartesianGrid stroke={CHART_GRID} vertical={false} />
      <XAxis
        dataKey={widget.config.x}
        tickFormatter={(value) => formatAxisTick(value)}
        tickMargin={8}
        axisLine={{ stroke: CHART_BASELINE }}
        tickLine={false}
        stroke={CHART_TICK}
        fontSize={CHART_TICK_FONT_SIZE}
        fontFamily={CHART_TICK_FONT_FAMILY}
        minTickGap={CHART_TICK_MIN_GAP}
        interval={CHART_CATEGORY_TICK_INTERVAL}
      />
      <YAxis
        tickFormatter={(value) =>
          formatAxisTick(value, { format: valueFormat === "auto" ? "compact" : valueFormat })
        }
        tickMargin={8}
        axisLine={false}
        tickLine={false}
        stroke={CHART_TICK}
        fontSize={CHART_TICK_FONT_SIZE}
        fontFamily={CHART_TICK_FONT_FAMILY}
        width={40}
      />
      <Tooltip
        content={<ChartTooltip valueFormat={valueFormat} />}
        cursor={CHART_CURSOR_LINE}
      />
      {widget.config.y.map((field, index) => {
        const color = colorForSeries(index);
        return (
          <Area
            key={field}
            type="monotone"
            dataKey={field}
            // Single shared stackId — see component docstring.
            stackId="stack"
            stroke={color}
            strokeWidth={1.5}
            // A flat fill: the design system has no gradients.
            fill={color}
            fillOpacity={0.35}
            // No per-point dots on stacked area: with several series the
            // dot grid quickly out-shouts the stacked shape.
            dot={false}
            activeDot={{ r: 3, stroke: CHART_ACTIVE_DOT_RING }}
            name={field}
            isAnimationActive={false}
          />
        );
      })}
    </AreaChart>
  );
}

interface HistogramChartBodyProps {
  widget: Extract<DashboardWidget, { type: "histogram" }>;
  rows: WidgetDataRow[];
}

/**
 * Recharts `<BarChart>` body for the `histogram` widget kind.
 *
 * The widget contract is the same one Grafana and Honeycomb's histogram
 * panels follow: the SQL author returns one row per bin (a `bin` column
 * with the left edge or label, plus a `count` column with the bucket
 * size). When `bin_width` is set we know the bin column is a numeric
 * left-edge and we render the X axis on a numeric linear scale so the
 * tick spacing reads in the original units; otherwise we fall back to a
 * categorical X axis that just labels each bar with its bin value.
 *
 * Bars touch (no spacing) regardless of mode — the visual signature of
 * a histogram vs. a categorical bar chart.
 */
function HistogramChartBody({ widget, rows }: HistogramChartBodyProps) {
  const { bin, count, bin_width, value_format, bin_format } = widget.config;
  const valueFormat: WidgetValueFormat = value_format ?? "compact";
  // `bin_format` defaults to `auto` when the bin column is numeric (so a
  // bin start of 240 renders as "240" rather than "240.0"); for string
  // bins we pass through the raw value (no formatting) because we have
  // no idea what shape the label is in.
  const isNumericBinAxis =
    bin_width !== undefined &&
    rows.length > 0 &&
    typeof rows[0]?.[bin] === "number";
  const effectiveBinFormat: WidgetValueFormat | undefined =
    bin_format ?? (isNumericBinAxis ? "auto" : undefined);

  // Render the X-axis tick label using the bin formatter. We only call
  // through the `formatValue` helper for numeric bins; string bins
  // either use the explicit `bin_format` (treating them as numeric-ish)
  // or pass through unchanged.
  const formatBinTick = (value: unknown): string => {
    if (typeof value === "number") {
      return formatValue(value, { format: effectiveBinFormat ?? "auto" });
    }
    if (effectiveBinFormat) {
      return formatValue(value, { format: effectiveBinFormat });
    }
    return value === null || value === undefined ? "" : String(value);
  };

  // Tooltip label: the bin column is a single value, so we render it
  // through the same formatter the X-axis uses for consistency. If the
  // numeric bin width is known, suffix the tooltip with the bin's right
  // edge so the user sees the full range — matches Grafana / Honeycomb.
  const formatTooltipLabel = (label: string | number | undefined): string => {
    if (label === undefined || label === null) return "";
    if (typeof label === "number" && bin_width !== undefined) {
      const start = formatBinTick(label);
      const end = formatBinTick(label + bin_width);
      return `${start} – ${end}`;
    }
    return formatBinTick(label);
  };

  // Compute the numeric domain so a histogram with a sparse middle bin
  // doesn't compress into the wrong x-range. We bound the right edge by
  // the last bin's right-edge (`max + bin_width`) so the final bar isn't
  // clipped at the chart edge.
  const numericDomain: [number, number] | undefined =
    isNumericBinAxis && bin_width !== undefined
      ? (() => {
          const numericValues = rows
            .map((row) => row[bin])
            .filter((v): v is number => typeof v === "number" && Number.isFinite(v));
          if (numericValues.length === 0) return undefined;
          const lo = Math.min(...numericValues);
          const hi = Math.max(...numericValues);
          return [lo, hi + bin_width];
        })()
      : undefined;

  const barColor = colorForSeries(0);

  return (
    <BarChart
      data={rows as unknown as object[]}
      margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
      // Bars touch on both axes — that's the visual signature of a
      // histogram. `barCategoryGap` controls the gap between adjacent
      // category buckets and `barGap` controls the gap between bars
      // within a single bucket; both must be 0 for a "real" histogram
      // look.
      barCategoryGap={0}
      barGap={0}
    >
      <CartesianGrid
        stroke={CHART_GRID}
        // Drop the vertical grid lines — they read as noise when bars
        // are touching. Leave the horizontal grid so the y-axis tick
        // values still land on a visible reference line.
        vertical={false}
      />
      {isNumericBinAxis && numericDomain ? (
        <XAxis
          type="number"
          dataKey={bin}
          domain={numericDomain}
          // Force ticks at every bin's left edge so the labels read as
          // bin starts (matches the Grafana histogram panel's default).
          // We cap at ~12 ticks to avoid label collisions on dense
          // histograms — recharts auto-thins from there.
          ticks={
            rows.length <= 12
              ? rows
                  .map((row) => row[bin])
                  .filter((v): v is number => typeof v === "number" && Number.isFinite(v))
              : undefined
          }
          tickFormatter={(value: unknown) => formatBinTick(value)}
          tickMargin={8}
          axisLine={{ stroke: CHART_BASELINE }}
          tickLine={false}
          stroke={CHART_TICK}
          fontSize={CHART_TICK_FONT_SIZE}
          fontFamily={CHART_TICK_FONT_FAMILY}
          minTickGap={CHART_TICK_MIN_GAP}
        />
      ) : (
        <XAxis
          type="category"
          dataKey={bin}
          tickFormatter={(value: unknown) => formatBinTick(value)}
          tickMargin={8}
          axisLine={{ stroke: CHART_BASELINE }}
          tickLine={false}
          stroke={CHART_TICK}
          fontSize={CHART_TICK_FONT_SIZE}
          fontFamily={CHART_TICK_FONT_FAMILY}
          minTickGap={CHART_TICK_MIN_GAP}
          interval="preserveStartEnd"
        />
      )}
      <YAxis
        tickFormatter={(value: unknown) => formatAxisTick(value, { format: "compact" })}
        tickMargin={8}
        axisLine={false}
        tickLine={false}
        stroke={CHART_TICK}
        fontSize={CHART_TICK_FONT_SIZE}
        fontFamily={CHART_TICK_FONT_FAMILY}
        width={40}
        // `allowDecimals=false` keeps the count axis on whole-number
        // ticks. A histogram count is always integer-valued; floating
        // ticks ("12.5 episodes") read as a bug.
        allowDecimals={false}
      />
      <Tooltip
        content={
          <ChartTooltip
            valueFormat={valueFormat}
            formatLabel={formatTooltipLabel}
          />
        }
        cursor={CHART_CURSOR_BAND}
      />
      <Bar
        dataKey={count}
        fill={barColor}
        name={count}
        // Skip the entrance animation for the same reason the line
        // widget does: dashboards refetch frequently and a tween-on-
        // every-refresh quickly becomes a flicker.
        isAnimationActive={false}
      />
    </BarChart>
  );
}

interface SqlTableProps {
  columns: string[];
  rows: WidgetDataRow[];
  hiddenColumns?: string[];
  onRowClick?: (row: WidgetDataRow) => void;
  getRowClassName?: (row: WidgetDataRow) => string | undefined;
}

function SqlTable({ columns, rows, hiddenColumns, onRowClick, getRowClassName }: SqlTableProps) {
  const hidden = useMemo(() => new Set(hiddenColumns ?? []), [hiddenColumns]);
  const tableColumns = useMemo<ColumnDef<WidgetDataRow>[]>(() => {
    return columns
      .filter((column) => !hidden.has(column))
      .map((column) => ({
        id: column,
        header: column,
        accessorFn: (row) => row[column],
        cell: (info) => formatCell(info.getValue()),
      }));
  }, [columns, hidden]);

  return (
    <DataTable<WidgetDataRow>
      columns={tableColumns}
      data={rows}
      onRowClick={onRowClick}
      getRowClassName={getRowClassName}
      pageSize={Math.min(10, Math.max(rows.length, 1))}
      emptyMessage="No rows returned"
      fixedLayout
    />
  );
}

function isSelectedRow(
  widget: DashboardWidget,
  row: WidgetDataRow,
  params?: Record<string, DashboardParameterValue>,
): boolean {
  if (!params) return false;
  const rowClick = widget.interactions?.find((interaction) => interaction.event === "row_click");
  // `set_parameters` is optional on WidgetInteraction now (point_click can be
  // a pure `open_url` action), so guard against undefined before iterating.
  if (!rowClick?.set_parameters) return false;
  const entries = Object.entries(rowClick.set_parameters).filter(([paramId]) => {
    const value = params[paramId];
    return value !== undefined && value !== null && value !== "";
  });
  if (entries.length === 0) return false;
  return entries.every(([paramId, expression]) => {
    return String(params[paramId]) === String(resolveRowValueExpression(expression, row));
  });
}

function deriveInitials(input: unknown): string {
  if (input === null || input === undefined) return "?";
  const text = String(input).trim();
  if (!text) return "?";
  const parts = text.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return text.slice(0, 2).toUpperCase();
}

interface EntityPickerBodyProps {
  widget: Extract<DashboardWidget, { type: "entity_picker" }>;
  data: WidgetDataResult;
  params?: Record<string, DashboardParameterValue>;
  onInteraction?: (row: WidgetDataRow, point?: WidgetInteractionPointContext) => void;
}

function EntityPickerBody({ widget, data, params, onInteraction }: EntityPickerBodyProps) {
  const config = widget.config;
  const sortBy = config.sort_by ?? config.metrics?.[0]?.value;
  const sortDir = config.sort_dir ?? "desc";

  const sortedRows = useMemo(() => {
    if (!sortBy) return data.rows;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...data.rows].sort((a, b) => {
      const av = a[sortBy];
      const bv = b[sortBy];
      const an = typeof av === "number" ? av : Number(av);
      const bn = typeof bv === "number" ? bv : Number(bv);
      const aIsNum = Number.isFinite(an);
      const bIsNum = Number.isFinite(bn);
      if (aIsNum && bIsNum) return (an - bn) * dir;
      // Mixed / text — fall back to lexicographic on the stringified value.
      return String(av ?? "").localeCompare(String(bv ?? "")) * dir;
    });
  }, [data.rows, sortBy, sortDir]);

  const cols = config.columns ?? 4;
  const gridClass =
    cols === 2 ? styles.entityGridCols2 : cols === 3 ? styles.entityGridCols3 : styles.entityGridCols4;

  const handleClick = onInteraction
    ? (row: WidgetDataRow) => onInteraction(row)
    : undefined;
  const interactive = Boolean(handleClick);

  if (data.isLoading) {
    return (
      <div className={`${styles.entityGrid} ${gridClass}`}>
        {Array.from({ length: cols * 2 }).map((_, idx) => (
          <div key={idx} className={styles.entityCard}>
            <div className={styles.entityCardHeader}>
              <div className={`${styles.entityAvatar} ${styles.skeleton}`} />
              <div className={styles.entityLabels}>
                <div className={`${styles.skeleton}`} style={{ height: 12, width: 100 }} />
                <div
                  className={`${styles.skeleton}`}
                  style={{ height: 10, width: 60, marginTop: "var(--space-1)" }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (sortedRows.length === 0) {
    return <div className={styles.entityEmpty}>No entities returned for this query.</div>;
  }

  return (
    <div className={`${styles.entityGrid} ${gridClass}`} role={interactive ? "listbox" : undefined}>
      {sortedRows.map((row, idx) => {
        const value = row[config.value];
        const label = row[config.label];
        const sublabel = config.sublabel ? row[config.sublabel] : undefined;
        const selected = isSelectedRow(widget, row, params);
        const className = `${styles.entityCard} ${selected ? styles.entityCardSelected : ""}`.trim();
        const key = String(value ?? `entity-${idx}`);
        const commonProps = {
          className,
          "aria-selected": interactive ? selected : undefined,
          role: interactive ? "option" : undefined,
        } as const;
        const body = (
          <>
            <div className={styles.entityCardHeader}>
              <span className={styles.entityAvatar} aria-hidden>
                {deriveInitials(label ?? value)}
              </span>
              <div className={styles.entityLabels}>
                <span className={styles.entityLabel}>{label != null && label !== "" ? String(label) : "—"}</span>
                {sublabel != null && sublabel !== "" ? (
                  <span className={styles.entitySublabel}>{String(sublabel)}</span>
                ) : null}
              </div>
            </div>
            {config.metrics && config.metrics.length > 0 ? (
              <div className={styles.entityMetrics}>
                {config.metrics.map((metric) => {
                  const raw = row[metric.value];
                  return (
                    <div key={metric.id} className={styles.entityMetric}>
                      <span className={styles.entityMetricLabel}>{metric.label}</span>
                      <span className={styles.entityMetricValue}>
                        {raw === null || raw === undefined
                          ? "—"
                          : formatValue(raw, { format: metric.format ?? "auto" })}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </>
        );
        if (handleClick) {
          return (
            <button
              key={key}
              type="button"
              {...commonProps}
              onClick={() => handleClick(row)}
            >
              {body}
            </button>
          );
        }
        return (
          <div key={key} {...commonProps}>
            {body}
          </div>
        );
      })}
    </div>
  );
}

function formatCell(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "number") return formatValue(value, { format: "auto" });
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "string" && isLikelyTimestamp(value)) {
    return formatTimestamp(value);
  }
  const text = String(value);
  if (text.length > 32) return `${text.slice(0, 14)}…${text.slice(-8)}`;
  return text;
}
